import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TOOLBAR_HEIGHT } from '../../../filmstrip/constants';
import { getVerticalViewMaxWidth, isFilmstripVisible } from '../../../filmstrip/functions.web';
import { isToolboxVisible } from '../../../toolbox/functions.web';
import { LAYOUTS } from '../../../video-layout/constants';
import { getCurrentLayout } from '../../../video-layout/functions.web';
import { setSgiReady, updateSGIState } from '../../actions';
import { SGI_DEBUG, SGI_EVENTS } from '../../constants';
import { buildSGIIframeUrl, getSGICredentials, getSgiSession, getSgiSharedState, isAllowedOrigin, isLocalParticipantSGIModerator, isSgiIframeReady } from '../../functions';
import './SGIContainer.css';
/**
 * SGIContainer Component.
 *
 * Renders the SGI iframe and handles postMessage communication
 * between Jitsi Meet and the SGI application.
 *
 * @returns {ReactElement|null}
 */
const SGIContainer = () => {
    const dispatch = useDispatch();
    const iframeRef = useRef(null);
    // Id of the session already announced to the iframe, so init is sent once per session.
    const initializedSessionRef = useRef(null);
    // Redux state
    const session = useSelector(getSgiSession);
    const sharedState = useSelector(getSgiSharedState);
    const isModerator = useSelector(isLocalParticipantSGIModerator);
    const iframeReady = useSelector(isSgiIframeReady);
    const credentials = useSelector(getSGICredentials);
    // Layout logic
    const filmstripVisible = useSelector(isFilmstripVisible);
    const filmstripWidth = useSelector(getVerticalViewMaxWidth);
    const toolboxVisible = useSelector(isToolboxVisible);
    const currentLayout = useSelector(getCurrentLayout);
    // Calculate dynamic styles
    const containerStyle = useMemo(() => {
        let right = 0;
        let bottom = 0; // Default when toolbox is hidden/not affecting
        // Calculate Right offset (Filmstrip)
        // The filmstrip is docked to the right edge in both of these layouts - they share
        // the same `.vertical-filmstrip, .stage-filmstrip` rule in the stylesheet. Tile view
        // is excluded there, so it correctly keeps the full width.
        if (filmstripVisible
            && (currentLayout === LAYOUTS.VERTICAL_FILMSTRIP_VIEW
                || currentLayout === LAYOUTS.STAGE_FILMSTRIP_VIEW)) {
            right = filmstripWidth;
        }
        // Calculate Bottom offset (Toolbox)
        if (toolboxVisible) {
            // TOOLBAR_HEIGHT is 72, plus some margin if needed
            bottom = TOOLBAR_HEIGHT + 10;
        }
        else {
            bottom = 10;
        }
        return {
            right: `${right}px`,
            bottom: `${bottom}px`
        };
    }, [filmstripVisible, filmstripWidth, toolboxVisible, currentLayout]);
    // Memoize iframe URL to prevent recalculating on every render
    // Only recalculate when session ID changes (new session started)
    const iframeUrl = useMemo(() => {
        if (!session?.active || !session?.sessionId) {
            return '';
        }
        return buildSGIIframeUrl(isModerator, credentials);
        // `isModerator` and `credentials` are read but deliberately left out of the
        // dependencies: rebuilding the URL mid-session would reload the iframe and
        // drop whatever the operator has on screen.
    }, [session?.sessionId, session?.active]);
    /**
     * Send postMessage to SGI iframe.
     */
    const postMessage = useCallback((type, payload) => {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) {
            console.warn('[SGI] Cannot send postMessage: iframe not ready');
            return;
        }
        const message = { type, payload };
        try {
            const targetOrigin = new URL(iframe.src).origin;
            iframe.contentWindow.postMessage(message, targetOrigin);
            if (SGI_DEBUG) {
                console.log('[SGI] postMessage sent:', type);
            }
        }
        catch (error) {
            console.error('[SGI] Failed to send postMessage:', error);
        }
    }, []);
    /**
     * Handle incoming postMessage from SGI iframe.
     */
    const handleMessage = useCallback((event) => {
        // Validate origin
        if (!isAllowedOrigin(event.origin)) {
            return;
        }
        const { type, payload } = event.data || {};
        if (!type?.startsWith('sgi:')) {
            return;
        }
        if (SGI_DEBUG) {
            console.log('[SGI] postMessage received:', type, payload);
        }
        switch (type) {
            case SGI_EVENTS.READY:
                // SGI iframe is loaded and ready
                dispatch(setSgiReady(true));
                break;
            case SGI_EVENTS.STATE_UPDATE:
                // Only moderator should broadcast state updates
                if (isModerator && payload) {
                    dispatch(updateSGIState(payload));
                }
                break;
            case SGI_EVENTS.STATE_REQUEST:
                // Late joiner requesting current state
                if (isModerator && sharedState) {
                    postMessage(SGI_EVENTS.STATE_UPDATE, sharedState);
                }
                break;
            default:
                if (SGI_DEBUG) {
                    console.log('[SGI] Unknown event type:', type);
                }
        }
    }, [dispatch, isModerator, sharedState, postMessage]);
    /**
     * Set up postMessage listener.
     */
    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);
    /**
     * Send session init when iframe is ready.
     *
     * Sent once per session. `sharedState` is read here to seed the iframe, but it must not
     * retrigger the init: it changes on every moderator action, which would otherwise re-init
     * the SGI sync context continuously. Subsequent state goes out as STATE_UPDATE below.
     */
    useEffect(() => {
        if (!session?.active) {
            initializedSessionRef.current = null;
            return;
        }
        if (!iframeReady || initializedSessionRef.current === session.sessionId) {
            return;
        }
        initializedSessionRef.current = session.sessionId;
        postMessage(SGI_EVENTS.SESSION_INIT, {
            sessionId: session.sessionId,
            moderatorId: session.moderatorId,
            moderatorName: session.moderatorName,
            isModerator,
            initialState: sharedState
        });
    }, [session, iframeReady, isModerator, sharedState, postMessage]);
    /**
     * Forward state updates to iframe (for viewers).
     */
    useEffect(() => {
        if (session?.active && iframeReady && !isModerator && sharedState) {
            postMessage(SGI_EVENTS.STATE_UPDATE, sharedState);
        }
    }, [session, iframeReady, isModerator, sharedState, postMessage]);
    // Don't render if no active session
    if (!session?.active) {
        return null;
    }
    return (React.createElement("div", { className: 'sgi-container', style: containerStyle },
        React.createElement("iframe", { allow: 'camera; microphone; fullscreen', className: 'sgi-iframe', ref: iframeRef, sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups', src: iframeUrl, title: 'SGI - Sistema de Gerenciamento de Imagens' })));
};
export default SGIContainer;
